import clsx from 'clsx';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShapeOption, ShoulderShapeModel, ShoulderShapeOptions, isLargeBMI, writeUserToStorage } from '@vestico/model';

import { FormValidationContext } from '../CommonAppContext';
import { ShapeSelect } from '../components/ShapeSelect';
import { useIsMounted } from '../hooks/mounted';
import { StepProps } from '../model/step';
import { UserContext } from '../model/userContext';
import StepContainer from './StepContainer';
import style from './shapeRoutes.module.css';

export const ShapeInputShoulder = (props: StepProps) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation('shared');

  const formValidationContext = useContext(FormValidationContext);
  const mounted = useIsMounted();

  const [submitting, setSubmitting] = useState(false);
  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const useLargeShapeShapes = useMemo(() => isLargeBMI(user?.height, user?.weight), [user?.height, user?.weight]);
  const shoulderShapeOptions = useMemo(() => ShoulderShapeOptions(useLargeShapeShapes), [useLargeShapeShapes]);

  const getDefaultShape = useCallback(() => {
    return user?.shoulderShape !== undefined ? user.shoulderShape : ShoulderShapeModel.Average;
  }, [user]);

  const [shoulderValue, setShoulderValue] = useState(getDefaultShape());
  const shoulderError = useMemo(
    () => (mounted.current && (shoulderValue < 0 || shoulderValue >= shoulderShapeOptions.length) ? t('LOC_ENTER_INFO_STEP_ERROR_SHOULDER') : ''),
    [shoulderValue, submitting, mounted]
  );

  useEffect(() => {
    if (!user) {
      return;
    }
    setShoulderValue(getDefaultShape());
  }, [user]);

  const formValid = useMemo(() => ![shoulderError].reduce((m, n) => m + n), [shoulderError]);

  useLayoutEffect(() => {
    if (!submitting || !user) {
      return;
    }

    formValidationContext.updateStep(props.path, formValid);

    if (!formValid) {
      setSubmitting(false);
      return;
    }

    user.shoulderShape = shoulderValue;
    writeUserToStorage(user);

    setSubmitting(false);
  }, [formValid, submitting, shoulderValue]);

  const onShapeUpdate = useCallback((selectedShapeOption: ShapeOption) => {
    setShoulderValue(selectedShapeOption.shape);
  }, []);

  const inputProps = { shapeOptions: shoulderShapeOptions, selectedOptionIndex: shoulderValue };
  return (
    <StepContainer title={props.title}>
      <form
        className={clsx(style.formLayout)}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitting(true);
        }}
      >
        <ShapeSelect title="Shoulder" error={shoulderError} inputProps={inputProps} onUpdate={onShapeUpdate} />
      </form>
    </StepContainer>
  );
};
